/* =========================================================================
 * HowItWorks — homepage section.
 *
 * Replaces the inline 4-card grid that lived in Presentation. Renders the
 * brief's four steps as a connected horizontal timeline (desktop) /
 * vertical stack (mobile). Each step has an icon disc + number + title +
 * body. A subtle dotted line ties them together so the eye reads the order
 * rather than four independent cards.
 *
 * Get-Rewarded is intentionally the last step — matching the brief's
 * positioning ("Rewards should be the final step, not the first").
 * ========================================================================= */

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

const STEPS = [
  {
    n: "01",
    icon: "smartphone",
    title: "Share",
    body: "Install the app. Pick what you want to contribute. Sessions are explicit — start when you're contributing, end when you're done.",
  },
  {
    n: "02",
    icon: "directions_run",
    title: "Participate",
    body: "Tag your activity (walking, driving, flying…), unlock 33 achievements, climb global and per-area leaderboards as the community grows.",
  },
  {
    n: "03",
    icon: "science",
    title: "Advance research",
    body: "Aggregated datasets reach universities, environmental agencies, and public-good organizations studying mobility, climate, and urban systems.",
  },
  {
    n: "04",
    icon: "favorite",
    title: "Give back",
    body: "When data is licensed, contributors may receive a share — or direct it to environmental causes. Recognition first, money optional.",
  },
];

export function HowItWorks() {
  return (
    <MKBox component="section" id="how-it-works" py={{ xs: 8, md: 12 }} px={{ xs: 2, md: 4 }}>
      <Container>
        <Grid container justifyContent="center" textAlign="center" mb={{ xs: 6, md: 8 }}>
          <Grid item xs={12} md={8}>
            <MKTypography
              variant="overline"
              color="primary"
              fontWeight="bold"
              sx={{ letterSpacing: "0.1em" }}
            >
              How it works
            </MKTypography>
            <MKTypography
              variant="h2"
              color="dark"
              mt={1}
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              From your phone to global research
            </MKTypography>
            <MKTypography variant="body1" color="text" mt={2} sx={{ maxWidth: 640, mx: "auto" }}>
              Four steps. The first three are about participation and impact — the fourth, about
              rewards, is optional and ranks last on purpose.
            </MKTypography>
          </Grid>
        </Grid>

        {/* Timeline */}
        <MKBox sx={{ position: "relative" }}>
          {/* Connecting line — horizontal on desktop, vertical on mobile.
              Drawn as a CSS gradient so it's subtle and resolution-independent. */}
          <MKBox
            aria-hidden
            sx={({ palette }) => ({
              position: "absolute",
              display: { xs: "block", md: "block" },
              // Desktop: horizontal line across the row of icons
              "&::before": {
                content: '""',
                position: "absolute",
                left: { xs: 23, md: "12.5%" },
                right: { xs: "auto", md: "12.5%" },
                top: { xs: 24, md: 28 },
                width: { xs: "1px", md: "auto" },
                height: { xs: "calc(100% - 48px)", md: "1px" },
                background: {
                  xs: `repeating-linear-gradient(to bottom, ${palette.grey[300]} 0 4px, transparent 4px 8px)`,
                  md: `repeating-linear-gradient(to right, ${palette.grey[300]} 0 6px, transparent 6px 12px)`,
                },
              },
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
            })}
          />

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {STEPS.map((s) => (
              <Grid item xs={12} md={3} key={s.n}>
                <MKBox
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row", md: "column" },
                    alignItems: { xs: "flex-start", md: "center" },
                    textAlign: { xs: "left", md: "center" },
                    gap: { xs: 2.5, md: 0 },
                    position: "relative",
                  }}
                >
                  {/* Icon disc */}
                  <MKBox
                    sx={({ palette }) => ({
                      flexShrink: 0,
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: palette.white.main,
                      border: `1.5px solid ${palette.primary.main}`,
                      color: palette.primary.main,
                      boxShadow: "0 4px 10px -4px rgba(37, 99, 255, 0.3)",
                      // Sits above the connecting line
                      position: "relative",
                      zIndex: 1,
                    })}
                  >
                    <Icon fontSize="small">{s.icon}</Icon>
                  </MKBox>

                  {/* Text block */}
                  <MKBox sx={{ mt: { xs: 0, md: 3 }, flex: 1 }}>
                    <MKTypography
                      variant="overline"
                      color="primary"
                      fontWeight="bold"
                      sx={{ letterSpacing: "0.1em" }}
                    >
                      Step {s.n}
                    </MKTypography>
                    <MKTypography
                      variant="h5"
                      color="dark"
                      fontWeight="bold"
                      mt={0.5}
                      mb={1}
                      sx={{ letterSpacing: "-0.01em" }}
                    >
                      {s.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.6 }}>
                      {s.body}
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </Grid>
            ))}
          </Grid>
        </MKBox>
      </Container>
    </MKBox>
  );
}
